/**
 * TraceKit configuration types
 */

import { TraceKitPlugin } from './plugins';
import { getApiBaseUrl, getEnvironment } from '../utils/env';
import { ThemeConfig } from './theme';

export interface TraceKitConfig {
  api: {
    baseUrl: string;
    timeout: number;
    retries: number;
  };
  
  features: {
    rag: boolean;
    reports: boolean;
    entities: boolean;
    sources: boolean;
    jobs: boolean;
    webhooks: boolean;
    cache: boolean;
    admin: boolean;
  };
  
  branding: {
    appName: string;
    logo: string;
    logoUrl?: string; // URL or path to logo (replaces 'logo')
    logoAlt?: string; // Alt text for logo
    /** Optional: use a different logo only on the marketing landing page (e.g. full wordmark). */
    landingLogoUrl?: string;
    /** Optional: alt text for landing logo. */
    landingLogoAlt?: string;
    favicon: string;
    primaryColor: string;
    secondaryColor: string;
    companyName?: string;
    platformTitle?: string; // Title shown in sidebar (defaults to appName)
    platformSubtitle?: string; // Subtitle shown in sidebar
    /** When false, landing navbar hides the subtitle line under the logo (e.g. full wordmark image). */
    showLandingNavbarSubtitle?: boolean;
    /** If false, hide "Powered by X" in sidebar. If string, use as label. Omit for default (Keystone AI / Sparc AI). */
    poweredBy?: string | false;
    /**
     * Display name used in feedback email subjects and related copy.
     * Prefer this over hardcoding "Keystone RFP Agent" — e.g. "GovEarner", "SPARC RFP".
     * Falls back to platformTitle / appName when omitted.
     */
    feedbackProductName?: string;
  };
  
  theme?: ThemeConfig;
  
  deployment: {
    environment: 'development' | 'staging' | 'production';
    domain: string;
    enableAnalytics: boolean;
  };
  
  plugins: TraceKitPlugin[];
  
  customization?: {
    defaultTenantId?: string;
    defaultProjectId?: string;
    hideTenantSelector?: boolean;
    /** Permission required to see tenant/project dropdowns (e.g. 'users.manage'). Only users with this permission see selectors; others get hardcoded tenant/project from config. */
    showWorkspaceSelectorsPermission?: string;
    customRoutes?: Array<{ path: string; component: React.ComponentType<any> }>;
    searchSampleQueries?: string[];
    entityTypes?: string[];
    featureFlags?: {
      // RFP Agent feature flags - UI/UX Features
      enableCalendarView?: boolean;              // Calendar view in opportunities dashboard
      enableCustomSections?: boolean;             // Custom sections tab in reports
      enableWordCountOptimizer?: boolean;        // Word count optimizer (counts always visible)
      enableQualityAssurance?: boolean;          // Quality assurance tab in reports
      enableReviewWorkflow?: boolean;             // Review workflow tab in reports
      enableAvailabilityCalendar?: boolean;       // Availability calendar tab in staff management
      enableQualificationsCerts?: boolean;        // Qualifications & certifications tab in staff management
      enableExecutiveDashboard?: boolean;         // Executive dashboard page
      enableWorkPlanExecution?: boolean;          // Work plan execution dashboard page
      enableProposalSearch?: boolean;             // Proposal Search tab in Proposal Management (false = hidden for all users, e.g. Keystone)
      enableWorkplanSearch?: boolean;             // Search Workplans tab in Work Plan & Execution (false = hidden for Keystone)
      /** When false, hide the Opportunities Dashboard import (CSV upload). Export and Custom Proposal stay visible. */
      enableOpportunitiesImport?: boolean;

      // Report viewer tab visibility (KEYSTONE_CLIENT_BUGS_17 / KBF-059)
      showCommentsAndReviewTab?: boolean;        // Comments & Review tab (false = hidden for Keystone)
      showAnalyticsTab?: boolean;                // Analytics tab (false = hidden for Keystone)
      showActionsTab?: boolean;                  // Actions tab (regenerate section; false = hidden for Keystone, redundant with Custom Sections)
      showSharingTab?: boolean;                  // Sharing tab in report viewer (false = hidden for Keystone)
      // Staff Management as standalone nav item (KEYSTONE_CLIENT_BUGS_17 / KBF-065)
      showStaffManagementStandalone?: boolean;   // Standalone Staff Management page in nav (false = only in Staff Assignments tab)
      
      // Government Data Center – hide USA/Canada/International/Europe zone buttons for Keystone
      showGovernmentDataZoneButtons?: boolean;  // Zone filter buttons in Government Data Center header (false = hidden for Keystone)
      /** SPARC-009: 7-zone geographic filter in Government Data header (SPARC on, Keystone off). */
      enableGovernmentDataGeographicZones?: boolean;
      
      // RFP Agent feature flags - Template Generation Methods
      enableProductionTemplate?: boolean;        // AI-Derived (Production) template generation (NEW - Jan 2026)
      enableLegacyTemplate?: boolean;            // Legacy AI-Derived template generation (backward compatibility)

      // Home page mode
      workspaceHomePage?: boolean;               // Show workspace/project selector at '/' instead of HomeDashboard (e.g. SPARC multi-project deployments)

      /** When true, Data Pipeline (/data) is available to any authenticated user without data_management.read (e.g. GovEarner SPARC). */
      dataPipelineOpenAccess?: boolean;
      /** SPARC-012: TEC Queue workflow for internal proposal review (SPARC-only). */
      enableTecWorkflow?: boolean;
      /** WS3: Embed costing-platform dashboard + suitability cost estimates (SPARC GovEarner only). */
      enableCostingPlatform?: boolean;
      /** When false, Weekly Review tab is hidden except for weeklyReviewDevEmails. */
      enableWeeklyReview?: boolean;
      /**
       * SPARC GovEarner only. When true, Opportunities defaults to software-defined
       * tenders (data.sparc_software_defined). Keystone/CSM omit this flag.
       */
      defaultSoftwareDefinedFilter?: boolean;
      /**
       * When false, Universal AI Search hides the "Smart Query Routing" card (entity type chips).
       * Default is true when omitted. Grant-finder style demos set false to reduce noise.
       */
      showUniversalSearchEntityRouting?: boolean;
    };
    /** Role names that can use Integrations Preview/Sync (e.g. admin_tech only, not admin). Defaults to ['admin_tech'] when absent. */
    integrationsTechRoles?: string[];
    /** When enableWorkPlanExecution is false, only these emails see the Work Plan & Execution nav (e.g. devs like parsa@alpyne.ai). */
    workPlanExecutionDevEmails?: string[];
    /** When set, Work Plan & Execution nav is also visible to users with this permission (e.g. users.manage = Tech Admin and above). Hidden for Admin or lower if this is the only condition. */
    workPlanVisibleToPermission?: string;
    /** When enableWeeklyReview is false, only these emails see the Weekly Review tab. */
    weeklyReviewDevEmails?: string[];
    /**
     * Landing geographic zone for ALL-access users (e.g. SPARC GovEarner `canada`).
     * Omit on Keystone/CSM so ALL-access still lands on World.
     */
    defaultGeographicZone?: string;
    /**
     * Permission required to see technical Settings tabs (Tenant Config, Cache, Webhooks, Document Gaps, QA & Observability, Retrieval & Search).
     * When set, only users with this permission see those tabs; others see Workspace, Policy Packs, Notifications, System & Appearance only.
     * Same permission as Data Pipeline (e.g. data_management.read for tech admin). Used by Keystone on-prem to hide technical tabs from admin/writer/viewer.
     */
    settingsTechnicalTabsPermission?: string;
    /** Nav and page title for Government Data / Opportunities (e.g. 'Opportunities Dashboard' for Keystone; default 'Government Tenders' / 'Government Data Center'). */
    opportunitiesDashboardLabel?: string;
    /** Label for canonical data tab in Work Plan & Execution (e.g. 'Baseline Costs, Suppliers & Service Lines' for Keystone; default 'Canonical Data'). */
    canonicalDataLabel?: string;
    /** Optional list of MCP/external data integrations for UI (e.g. "Data sources", "Connected systems"). Backend routing is driven by policy pack mcp_integrations. */
    mcpIntegrations?: Array<{ id: string; label: string; description?: string }>;
    /**
     * App IDs to display in AppPicker. When omitted, all built-in apps are shown (internal/dev mode).
     * Use to limit the picker to only apps included in a client delivery (e.g. ['keystone-rfp', 'baseline']).
     */
    appPickerApps?: string[];
    /** Email recipients for in-app feedback notifications (HeaderFeedbackButton). */
    feedbackRecipients?: string[];
    /**
     * Hostnames allowed as opportunity-email (and similar outbound) recipients.
     * Omit the @. Subdomains of a listed host are allowed.
     * When omitted, the RFP plugin keeps Keystone-only (`keystoneenvironmental.ca`).
     */
    allowedEmailDomains?: string[];
    /** Support contact email shown in help prompts (e.g. AddCitationModal SharePoint sync request). */
    supportEmail?: string;
    /** WS3: Base URL for embedded costing-dashboard (GovEarner / SPARC). */
    costingPlatformUrl?: string;
  };
}

export const DEFAULT_CONFIG: TraceKitConfig = {
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: 120000,
    retries: 3,
  },
  features: {
    rag: true,
    reports: true,
    entities: true,
    sources: true,
    jobs: true,
    webhooks: false,
    cache: false,
    admin: false,
  },
  branding: {
    appName: 'TraceKit',
    logo: '/logo.svg',
    logoUrl: '/logo.svg',
    logoAlt: 'TraceKit Logo',
    favicon: '/favicon.ico',
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    platformTitle: 'TraceKit',
    platformSubtitle: 'Evidence Platform',
  },
  theme: {
    defaultMode: 'dark',
    allowToggle: true,
  },
  deployment: {
    environment: getEnvironment(),
    domain: 'localhost:3000',
    enableAnalytics: false,
  },
  plugins: [],
};


